import { useState } from "react";
import {
  ShieldCheck,
  FileText,
  Car,
  CalendarDays,
  User,
  Camera,
  Hash,
  ClipboardList,
  ClipboardCheck,
  BadgeCheck,
  HeartPulse,
  Truck,
  PoundSterling,
  CarTaxiFront,
} from "lucide-react";
import { PageHero, WhatsAppCta } from "../components/PageSections";
import { useSeo } from "../lib/seo";
import { BodyshopSection } from "../components/BodyshopSection";
import { FormField, formInputClass } from "../components/forms/fields";
import { repairImages, claimRepairImages } from "../data/media";
import { sendContactForm, SUCCESS_MESSAGE } from "../lib/contactForm";

const services = [
  { icon: Car, title: "Courtesy Car", text: "Stay mobile with a replacement vehicle while yours is off the road." },
  { icon: BadgeCheck, title: "Insurance Approved", text: "Work carried out to full insurance-approved standards." },
  { icon: HeartPulse, title: "Personal Injury", text: "Support with the personal injury elements of your claim." },
  { icon: Truck, title: "Free Recovery & Storage", text: "Your vehicle recovered from the scene and kept in secure storage — at no cost to you." },
  { icon: PoundSterling, title: "No Excess To Pay", text: "On eligible non-fault claims there's no excess for you to pay." },
  { icon: ClipboardList, title: "Motor Claims Support", text: "Clear guidance through every stage of your motor claim." },
  { icon: CarTaxiFront, title: "Taxi & PCO Claims", text: "Specialist support for taxi and PCO drivers and vehicles." },
];

const steps = [
  { title: "Make an Enquiry", text: "Tell us what has happened." },
  { title: "Provide Your Details", text: "Share the relevant claim information." },
  { title: "Information Reviewed", text: "We review the information you provide." },
  { title: "Claim Support", text: "We help guide you through the next steps." },
];

const mayNeed = [
  { icon: FileText, label: "Insurance / policy details" },
  { icon: Car, label: "Vehicle registration" },
  { icon: CalendarDays, label: "Incident details & date" },
  { icon: User, label: "Driver details" },
  { icon: Camera, label: "Photos & documents" },
  { icon: Hash, label: "Relevant reference numbers" },
];

const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  policyDetails: "",
  vehicleReg: "",
  incidentDate: "",
  whatHappened: "",
  referenceNumber: "",
};

const ClaimEnquiryForm = () => {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [honeypot, setHoneypot] = useState("");

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const validate = () => {
    const e = {};
    if (!form.fullName.trim()) e.fullName = "Please enter your full name.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Please enter a valid email address.";
    if (!/^[0-9+\s]{7,}$/.test(form.phone)) e.phone = "Please enter a valid phone number.";
    if (!form.vehicleReg.trim()) e.vehicleReg = "Please enter your vehicle registration.";
    if (!form.incidentDate) e.incidentDate = "Please select the incident date.";
    if (!form.whatHappened.trim()) e.whatHappened = "Please tell us briefly what happened.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    if (sending) return;
    if (!validate()) return;
    setSending(true);
    setSendError("");
    try {
      await sendContactForm({
        subject: "New EDT Motor Claim Enquiry",
        from_name: form.fullName,
        botcheck: honeypot,
        fields: {
          Name: form.fullName,
          email: form.email,
          Phone: form.phone,
          "Vehicle Registration": form.vehicleReg,
          "Insurance / Policy Details": form.policyDetails,
          "Incident Date": form.incidentDate,
          "What Happened": form.whatHappened,
          "Reference Number": form.referenceNumber,
        },
      });
      setSubmitted(true);
    } catch (err) {
      setSendError(err.message);
    } finally {
      setSending(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="claim-form-success-message" className="border border-[rgba(201,162,75,0.3)] px-6 py-14 text-center">
        <ClipboardCheck size={26} className="mx-auto text-[#c9a24b]" />
        <p className="mt-4 text-sm text-[#f2ede3]">{SUCCESS_MESSAGE}</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-10 grid gap-6 sm:grid-cols-2" data-testid="claim-enquiry-form">
      <input type="text" name="botcheck" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
      <FormField label="Full Name" error={errors.fullName} testId="claim-form-name">
        <input className={formInputClass} value={form.fullName} onChange={update("fullName")} data-testid="claim-form-name-input" />
      </FormField>
      <FormField label="Email Address" error={errors.email} testId="claim-form-email">
        <input type="email" className={formInputClass} value={form.email} onChange={update("email")} data-testid="claim-form-email-input" />
      </FormField>
      <FormField label="Phone Number" error={errors.phone} testId="claim-form-phone">
        <input className={formInputClass} value={form.phone} onChange={update("phone")} data-testid="claim-form-phone-input" />
      </FormField>
      <FormField label="Vehicle Registration" error={errors.vehicleReg} testId="claim-form-vehicle-reg">
        <input className={formInputClass} value={form.vehicleReg} onChange={update("vehicleReg")} data-testid="claim-form-vehicle-reg-input" />
      </FormField>
      <FormField label="Insurance / Policy Details" testId="claim-form-policy">
        <input className={formInputClass} value={form.policyDetails} onChange={update("policyDetails")} data-testid="claim-form-policy-input" />
      </FormField>
      <FormField label="Incident Date" error={errors.incidentDate} testId="claim-form-incident-date">
        <input type="date" className={formInputClass} value={form.incidentDate} onChange={update("incidentDate")} data-testid="claim-form-incident-date-input" />
      </FormField>
      <FormField label="What Happened?" error={errors.whatHappened} testId="claim-form-what-happened">
        <textarea rows={3} className={formInputClass} value={form.whatHappened} onChange={update("whatHappened")} data-testid="claim-form-what-happened-input" />
      </FormField>
      <FormField label="Reference Number (if you have one)" testId="claim-form-reference">
        <input className={formInputClass} value={form.referenceNumber} onChange={update("referenceNumber")} data-testid="claim-form-reference-input" />
      </FormField>
      {sendError && (
        <p data-testid="claim-form-send-error" className="text-sm text-[#e08a6b] sm:col-span-2">
          {sendError}
        </p>
      )}
      <button
        type="submit"
        disabled={sending}
        data-testid="claim-form-submit-button"
        className="mt-2 inline-flex w-full items-center justify-center gap-2 bg-[#c9a24b] px-8 py-3.5 text-xs font-bold uppercase tracking-[0.18em] text-[#0a0a09] transition-colors duration-200 hover:bg-[#e6c87a] disabled:cursor-not-allowed disabled:opacity-60 sm:col-span-2"
      >
        {sending ? "Sending…" : "Submit Claim Enquiry"}
      </button>
    </form>
  );
};

export default function Claims() {
  useSeo(
    "Accident & Insurance Claims | EDT Solutions",
    "EDT provides clear motor claims support and vehicle bodywork repairs — help with claim forms, documentation, communication and guidance through every step of your insurance claim."
  );
  return (
    <main data-testid="claims-page">
      <PageHero
        label="Motor Claims Assist"
        heading="Motor Claims, Handled With Care"
        text="Dealing with a motor claim can be confusing and time-consuming. EDT provides clear, straightforward support — helping you understand the process, prepare the right information and stay informed at every stage."
        testIdPrefix="claims"
      />

      {/* What EDT can help with */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="max-w-2xl">
          <p className="section-label" data-testid="claims-help-label">
            How We Help
          </p>
          <h2
            data-testid="claims-help-heading"
            className="font-display mt-4 text-2xl leading-snug text-[#f2ede3] sm:text-[2rem]"
          >
            What EDT Can Help With
          </h2>
          <p className="mt-5 text-sm leading-relaxed text-[#a09a8c] sm:text-base">
            From the moment of an incident to the final repair, EDT provides the
            support that keeps you moving and takes the pressure off.
          </p>
        </div>
        <ul className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {services.map(({ icon: Icon, title, text }, i) => (
            <li
              key={title}
              data-testid={`claims-service-${i + 1}`}
              className="group border border-[rgba(201,162,75,0.22)] bg-[#111110] p-6 transition-colors duration-200 hover:border-[#c9a24b]"
            >
              <span className="inline-flex h-11 w-11 items-center justify-center border border-[rgba(201,162,75,0.35)] text-[#c9a24b] transition-colors duration-200 group-hover:bg-[#c9a24b] group-hover:text-[#0a0a09]">
                <Icon size={20} />
              </span>
              <h3 className="font-display mt-4 text-base text-[#f2ede3]">{title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-[#a09a8c]">{text}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* Process */}
      <section className="bg-[#0e0d0c]">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
          <h2
            data-testid="claims-process-heading"
            className="font-display text-xl text-[#f2ede3] sm:text-2xl"
          >
            How the Claims Process Works
          </h2>
          <ol className="mt-10 grid gap-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-8">
            {steps.map((step, i) => (
              <li key={step.title} data-testid={`claims-step-${i + 1}`}>
                <span className="font-display text-sm tracking-[0.2em] text-[#c9a24b]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <h3 className="font-display mt-3 text-base text-[#f2ede3]">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-[#a09a8c]">
                  {step.text}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* What you may need */}
      <section className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
        <div className="border-y border-[rgba(201,162,75,0.18)] py-8">
          <div className="flex items-center gap-3">
            <ClipboardList size={18} className="text-[#c9a24b]" />
            <h2
              data-testid="claims-may-need-heading"
              className="font-display text-xl text-[#f2ede3] sm:text-2xl"
            >
              What You May Need
            </h2>
          </div>
          <p className="mt-3 text-sm leading-relaxed text-[#a09a8c]">
            Having the following ready helps us review your situation faster:
          </p>
          <ul className="mt-6 grid gap-x-10 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
            {mayNeed.map(({ icon: Icon, label }, i) => (
              <li
                key={label}
                data-testid={`claims-may-need-item-${i + 1}`}
                className="flex items-center gap-3 text-sm text-[#a09a8c]"
              >
                <Icon size={15} className="shrink-0 text-[#c9a24b]" />
                {label}
              </li>
            ))}
          </ul>
        </div>
      </section>

      <WhatsAppCta
        heading="Start Your Claim Enquiry"
        text="Message us on WhatsApp, tell us what has happened, and we'll guide you through the next steps."
        testIdPrefix="claims"
      />

      {/* Bodyshop / Repair */}
      <BodyshopSection testId="claims-bodyshop-section" repairGallery={[...claimRepairImages, ...repairImages]} />

      {/* Claim Enquiry Form */}
      <section id="claim-form" className="border-t border-[rgba(201,162,75,0.22)] bg-[#111110] scroll-mt-20">
        <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 sm:py-24">
          <p className="section-label" data-testid="claim-form-label">
            Enquiry Form
          </p>
          <h2
            data-testid="claim-form-heading"
            className="font-display mt-4 text-2xl leading-snug text-[#f2ede3] sm:text-[2rem]"
          >
            Start Your Claim Enquiry
          </h2>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-[#a09a8c]">
            Tell us what happened and we&apos;ll be in touch to guide you through the
            next steps.
          </p>
          <ClaimEnquiryForm />
        </div>
      </section>
    </main>
  );
}
